import { useEffect, useRef, useState } from "react";
import {
  normalizeAgentStatusStreamFrame,
  type AgentStatusStreamEvent,
} from "../utils/agentStatusStream";

export interface UseAgentStatusStreamOptions {
  apiBaseUrl: string;
  /** Changes whenever the authenticated workspace changes so an old stream
   * cannot continue applying events to the newly selected workspace. */
  workspaceKey?: string | null;
  getAuthHeaders: () => Promise<Record<string, string>>;
  enabled: boolean;
  onAgentStatus: (event: AgentStatusStreamEvent) => void;
  /** Reconcile once after every successful connection. LISTEN/NOTIFY is not
   * replayable, so a reconnect may have missed either edge of a transition. */
  onConnected: () => void;
  fetchImpl?: typeof fetch;
  reconnectDelayMs?: number;
}

export interface AgentStatusStreamState {
  pushActive: boolean | null;
}

/** Subscribe to backend-reported workspace agent-status transitions. */
export function useAgentStatusStream(
  {
    apiBaseUrl,
    workspaceKey = null,
    getAuthHeaders,
    enabled,
    onAgentStatus,
    onConnected,
    fetchImpl,
    reconnectDelayMs = 1500,
  }: UseAgentStatusStreamOptions,
): AgentStatusStreamState {
  const [pushActive, setPushActive] = useState<boolean | null>(null);
  const getAuthHeadersRef = useRef(getAuthHeaders);
  getAuthHeadersRef.current = getAuthHeaders;
  const onAgentStatusRef = useRef(onAgentStatus);
  onAgentStatusRef.current = onAgentStatus;
  const onConnectedRef = useRef(onConnected);
  onConnectedRef.current = onConnected;

  useEffect(() => {
    if (!enabled) {
      setPushActive(null);
      return undefined;
    }

    const doFetch = fetchImpl ?? fetch;
    let active = true;
    let generation = 0;
    let abortController: AbortController | null = null;
    let reconnectTimer: number | null = null;
    let consecutiveFailures = 0;
    const isHidden = () => document.visibilityState === "hidden";

    const clearReconnectTimer = () => {
      if (reconnectTimer === null) return;
      window.clearTimeout(reconnectTimer);
      reconnectTimer = null;
    };

    const consume = async (
      body: ReadableStream<Uint8Array>,
      runGeneration: number,
    ) => {
      const reader = body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (
        active &&
        runGeneration === generation &&
        !isHidden()
      ) {
        const { done, value } = await reader.read();
        if (
          !active ||
          runGeneration !== generation ||
          isHidden()
        ) {
          return;
        }
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";

        for (const rawLine of lines) {
          const line = rawLine.endsWith("\r") ? rawLine.slice(0, -1) : rawLine;
          if (!line.startsWith("data:")) continue;
          const data = line.slice(5).trim();
          if (!data || data === "[DONE]") continue;
          try {
            const frame = normalizeAgentStatusStreamFrame(JSON.parse(data));
            if (
              !frame ||
              !active ||
              runGeneration !== generation
            ) {
              continue;
            }
            if (frame.type === "agent_status_snapshot") {
              setPushActive(frame.pushActive);
              frame.agents.forEach((event) =>
                onAgentStatusRef.current(event),
              );
            } else {
              onAgentStatusRef.current(frame);
            }
          } catch {
            // A malformed frame is not a reported state. Ignore it and keep
            // the last known value until the retained poll reconciles.
          }
        }
      }
    };

    const scheduleReconnect = (runGeneration: number, delayMs: number) => {
      if (
        !active ||
        runGeneration !== generation ||
        isHidden()
      ) {
        return;
      }
      clearReconnectTimer();
      reconnectTimer = window.setTimeout(() => {
        reconnectTimer = null;
        void connect(runGeneration);
      }, delayMs);
    };

    const connect = async (runGeneration: number): Promise<void> => {
      if (
        !active ||
        runGeneration !== generation ||
        isHidden()
      ) {
        return;
      }

      // Readiness belongs to this exact connection generation. Until its
      // first valid snapshot arrives, neither a previous worker nor a prior
      // connection is evidence that push is active here.
      setPushActive(null);
      const controller = new AbortController();
      abortController = controller;
      try {
        const authHeaders = await getAuthHeadersRef.current();
        if (
          !active ||
          runGeneration !== generation ||
          isHidden()
        ) {
          return;
        }
        const response = await doFetch(`${apiBaseUrl}/chat/agent-status/stream`, {
          method: "GET",
          headers: { ...authHeaders, Accept: "text/event-stream" },
          credentials: "include",
          signal: controller.signal,
        });
        if (!active || runGeneration !== generation) return;
        if (response.status === 401 || response.status === 403) {
          // A refusal is not a transient stream drop. The 15-second summary
          // poll remains available; do not hammer the authentication surface.
          return;
        }
        if (!response.ok || !response.body) {
          throw new Error(`Agent status stream refused (${response.status})`);
        }

        consecutiveFailures = 0;
        onConnectedRef.current();
        await consume(response.body, runGeneration);
        if (active && runGeneration === generation) {
          setPushActive(null);
        }
        scheduleReconnect(runGeneration, reconnectDelayMs);
      } catch (error) {
        if (
          !active ||
          runGeneration !== generation ||
          controller.signal.aborted
        ) {
          return;
        }
        setPushActive(null);
        consecutiveFailures += 1;
        const delayMs = Math.min(30_000, 2_000 * 2 ** (consecutiveFailures - 1));
        scheduleReconnect(runGeneration, delayMs);
      }
    };

    const start = () => {
      if (isHidden()) return;
      generation += 1;
      void connect(generation);
    };

    const stop = () => {
      generation += 1;
      clearReconnectTimer();
      abortController?.abort();
      abortController = null;
      setPushActive(null);
    };

    const handleVisibilityChange = () => {
      if (isHidden()) {
        stop();
      } else {
        start();
      }
    };

    start();
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      stop();
      active = false;
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [apiBaseUrl, enabled, fetchImpl, reconnectDelayMs, workspaceKey]);

  return { pushActive };
}
